import { organizationList } from "@ir/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Field } from "@/components/ui/Field";
import { Select } from "@/components/ui/Select";
import { OrganizationsTable } from "@/features/organizations/OrganizationsTable";
import { ApiError, apiFetch } from "@/lib/api";
import { authClient } from "@/lib/authClient";
import { optionalProp } from "@/lib/optionalProp";

export const Route = createFileRoute("/_auth/admin/organizaciones")({
  async beforeLoad() {
    const { data } = await authClient.getSession();
    if (data?.user.role !== "ir_admin") redirect({ to: "/insights", throw: true });
  },
  component: AdminOrganizationsPage,
});

function AdminOrganizationsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [creando, setCreando] = useState(false);
  const [nombre, setNombre] = useState("");
  const [kind, setKind] = useState<"internal" | "external">("external");
  const [errorAlta, setErrorAlta] = useState<string | null>(null);

  const organizations = useQuery({
    queryKey: ["organizations"],
    queryFn: () => apiFetch("/api/organizations", { schema: organizationList }),
    // Sin reintentos: un fallo se enseña, no se disimula con tres intentos.
    retry: false,
  });

  // Renombrar y borrar cambian lo que ven las demás pantallas: el selector de
  // acceso del formulario y la columna de organizaciones de usuarios leen esta
  // misma lista, y borrar una arrastra sus concesiones de study_access.
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["organizations"] });
    await queryClient.invalidateQueries({ queryKey: ["studies"] });
  };

  // Sin `onError`: el mensaje viaja hasta el diálogo, que lo pinta y se queda
  // abierto. Es lo que hace útil el 409 del borrado, que dice qué reasignar.
  const create = useMutation({
    mutationFn: (payload: { name: string; kind: "internal" | "external" }) =>
      apiFetch("/api/organizations", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: refresh,
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiFetch(`/api/organizations/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/organizations/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });

  const cerrarAlta = () => {
    setCreando(false);
    setNombre("");
    setKind("external");
    setErrorAlta(null);
  };

  const crear = async () => {
    setErrorAlta(null);
    try {
      await create.mutateAsync({ name: nombre.trim(), kind });
      cerrarAlta();
    } catch (fallo) {
      setErrorAlta(fallo instanceof ApiError ? fallo.message : t("common.error"));
    }
  };

  return (
    <>
      <PageHeader
        title={t("organizations.title")}
        subtitle={t("organizations.subtitle")}
        actions={
          <Button
            onClick={() => {
              setErrorAlta(null);
              setCreando(true);
            }}
          >
            {t("organizations.new")}
          </Button>
        }
      />

      <div className="px-8 py-6">
        <div className="overflow-x-auto rounded-[3px] border border-line bg-card">
          {organizations.isError && (
            <p className="p-4 text-nota text-warn">
              {organizations.error instanceof ApiError
                ? organizations.error.message
                : t("common.error")}
            </p>
          )}

          {organizations.isPending && (
            <p className="p-4 text-nota text-ink3">{t("common.loading")}</p>
          )}

          {organizations.data && organizations.data.items.length === 0 && (
            <p className="p-4 text-nota text-ink3">{t("organizations.empty")}</p>
          )}

          {organizations.data && organizations.data.items.length > 0 && (
            <OrganizationsTable
              organizations={organizations.data.items}
              onRename={async (id, name) => {
                await rename.mutateAsync({ id, name });
              }}
              onDelete={async (id) => {
                await remove.mutateAsync(id);
              }}
            />
          )}
        </div>
      </div>

      <Dialog open={creando} onOpenChange={cerrarAlta} title={t("organizations.createTitle")}>
        <div className="flex flex-col gap-3">
          <Field
            label={t("organizations.name")}
            value={nombre}
            onChange={(e) => {
              setNombre(e.target.value);
            }}
            aria-invalid={errorAlta ? true : undefined}
            {...optionalProp("hint", errorAlta ?? undefined)}
          />
          <Select
            label={t("organizations.kind")}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value === "internal" ? "internal" : "external");
            }}
          >
            <option value="external">{t("organizations.external")}</option>
            <option value="internal">{t("organizations.internal")}</option>
          </Select>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={cerrarAlta}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={nombre.trim().length === 0 || create.isPending}
            onClick={() => void crear()}
          >
            {t("common.save")}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
